import React, { useEffect, useState } from 'react'
import { Button, Form, Input, Modal, message } from 'antd'
import { CopyOutlined, LockOutlined, StarFilled } from '@ant-design/icons'
import {
  accessPromptTemplate,
  updatePromptTemplate,
  type PromptTemplateUpdatePayload,
} from '@/services/promptTemplateService'
import './PromptTemplateAccessModal.css'

const { TextArea } = Input

export interface PromptEditableResource {
  id: number
  name: string
  content: string
  description: string
  type?: string
  isDefault?: boolean
}

interface PromptTemplateAccessModalProps {
  open: boolean
  template: PromptEditableResource | null
  onClose: () => void
  onUpdated?: (template: PromptEditableResource) => void
  accessResource?: (id: number, password: string) => Promise<PromptEditableResource>
  saveResource?: (
    id: number,
    payload: PromptTemplateUpdatePayload
  ) => Promise<PromptEditableResource | void>
  resourceLabel?: string
  contentLabel?: string
  saveSuccessMessage?: string
}

const contentPreviewStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: '#f8fafc',
  padding: '24px',
  borderRadius: '12px',
  fontSize: '15px',
  lineHeight: '1.85',
  maxHeight: '58vh',
  overflow: 'auto',
}

const PromptTemplateAccessModal: React.FC<PromptTemplateAccessModalProps> = ({
  open,
  template,
  onClose,
  onUpdated,
  accessResource = accessPromptTemplate,
  saveResource = updatePromptTemplate,
  resourceLabel = '模板',
  contentLabel = '提示词内容',
  saveSuccessMessage = '提示词已更新',
}) => {
  const [passwordForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [verifiedPassword, setVerifiedPassword] = useState('')
  const [detail, setDetail] = useState<PromptEditableResource | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!open) {
      passwordForm.resetFields()
      editForm.resetFields()
      setDetail(null)
      setVerifiedPassword('')
      setIsEditing(false)
    }
  }, [editForm, open, passwordForm])

  const handleVerify = async () => {
    if (!template) {
      return
    }

    try {
      const values = await passwordForm.validateFields()
      setDetailLoading(true)
      const fullTemplate = await accessResource(template.id, values.password)
      setDetail(fullTemplate)
      setVerifiedPassword(values.password)
      editForm.setFieldsValue({
        name: fullTemplate.name,
        description: fullTemplate.description,
        content: fullTemplate.content,
      })
      message.success('密码验证成功')
    } catch (error: any) {
      message.error(error?.message || '密码错误或获取模板详情失败')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleSave = async () => {
    if (!detail) {
      return
    }

    try {
      const values = await editForm.validateFields()
      setSaving(true)
      const savedResource = await saveResource(detail.id, {
        ...values,
        password: verifiedPassword,
      })

      const nextDetail: PromptEditableResource = {
        ...detail,
        ...values,
        ...(savedResource || {}),
      }

      setDetail(nextDetail)
      setIsEditing(false)
      onUpdated?.(nextDetail)
      message.success(saveSuccessMessage)
    } catch (error: any) {
      message.error(error?.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  const handleCopyContent = async () => {
    if (!detail?.content) {
      message.warning('暂无可复制的提示词内容')
      return
    }

    try {
      await navigator.clipboard.writeText(detail.content)
      message.success('提示词已复制')
    } catch (error) {
      message.warning('复制失败，请手动选择文本复制')
    }
  }

  return (
    <Modal
      title={detail ? null : '密码验证'}
      open={open}
      onCancel={() => {
        if (detailLoading || saving) {
          return
        }
        onClose()
      }}
      width={detail ? 980 : 420}
      className="prompt-modal"
      okText={detail ? undefined : '验证并查看'}
      cancelText="取消"
      confirmLoading={detail ? saving : detailLoading}
      onOk={detail ? undefined : handleVerify}
      footer={
        detail
          ? isEditing
            ? [
                <Button
                  key="cancel-edit"
                  onClick={() => {
                    setIsEditing(false)
                    editForm.setFieldsValue({
                      name: detail.name,
                      description: detail.description,
                      content: detail.content,
                    })
                  }}
                >
                  取消
                </Button>,
                <Button key="save-edit" type="primary" loading={saving} onClick={handleSave}>
                  保存修改
                </Button>,
              ]
            : [
                <Button key="copy-detail" icon={<CopyOutlined />} onClick={handleCopyContent}>
                  复制提示词
                </Button>,
                <Button key="close-detail" onClick={onClose}>
                  关闭
                </Button>,
                <Button key="edit-detail" type="primary" icon={<LockOutlined />} onClick={() => setIsEditing(true)}>
                  修改提示词
                </Button>,
              ]
          : undefined
      }
    >
      {!detail ? (
        <>
          <div className="prompt-password-hint">
            <LockOutlined />
            <span>查看或修改{contentLabel}前，需要先输入管理密码。</span>
          </div>
          {template && (
            <div className="prompt-password-target">
              当前{resourceLabel}：<strong>{template.name}</strong>
            </div>
          )}
          <Form form={passwordForm} layout="vertical" style={{ marginTop: 20 }}>
            <Form.Item
              name="password"
              label="管理密码"
              rules={[{ required: true, message: '请输入管理密码' }]}
            >
              <Input.Password
                placeholder={`请输入密码后查看或修改${contentLabel}`}
                autoComplete="off"
                onPressEnter={() => {
                  void handleVerify()
                }}
              />
            </Form.Item>
          </Form>
        </>
      ) : (
        <div className="prompt-detail-card">
          {detail.type && (
            <div className={`prompt-detail-badge ${detail.type}`}>
              {detail.type === 'official' ? '🏆 官方模板' : '✨ 自定义模板'}
            </div>
          )}
          {detail.isDefault && (
            <div style={{ marginBottom: '12px', color: '#92400e', fontSize: '13px', fontWeight: 600 }}>
              <StarFilled style={{ marginRight: '6px' }} />
              系统默认模板
            </div>
          )}
          {isEditing ? (
            <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item
                name="name"
                label="名称"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input placeholder="例如：网文大神风格" />
              </Form.Item>
              <Form.Item
                name="description"
                label="描述"
                rules={[{ required: true, message: '请输入描述' }]}
              >
                <Input placeholder="简要描述这个模板的特点和用途" />
              </Form.Item>
              <Form.Item
                name="content"
                label={contentLabel}
                rules={[{ required: true, message: `请输入${contentLabel}` }]}
              >
                <TextArea rows={18} placeholder={`输入完整的${contentLabel}...`} />
              </Form.Item>
            </Form>
          ) : (
            <>
              <h2 className="prompt-detail-title">{detail.name}</h2>
              <div className="prompt-detail-section">
                <div className="prompt-detail-section-title">📝 简介</div>
                <div className="prompt-detail-description">{detail.description || '暂无简介'}</div>
              </div>
              <div className="prompt-detail-section">
                <div className="prompt-detail-section-title">📝 {contentLabel}</div>
                <div className="prompt-detail-content">
                  <pre style={contentPreviewStyle}>{detail.content || '暂无内容'}</pre>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}

export default PromptTemplateAccessModal
